import {
  area,
  bbox,
  center as findCenter,
  featureCollection as FeatureCollection,
  point as Point,
} from "@turf/turf"

const MAX_MAP_ZOOM = 15.5

export const getCenterOfFeature = feature => {
  if (!feature) {
    return null
  }

  const centerPoint = findCenter(FeatureCollection([feature]))
  return centerPoint.geometry.coordinates
}

export const centerMapOnFeatures = (map, center) => {
  if (!map || !center) {
    return false
  }

  map.flyTo({ center })
  return true
}

/**
 * Validates that bounds array contains valid finite numbers.
 * Mapbox fitBounds accepts two formats:
 * 1. Flat array (turf bbox): [west, south, east, north] (4 numbers)
 * 2. Nested array: [[lng1, lat1], [lng2, lat2]] (2 arrays of 2 numbers each)
 * @param {Array} bounds - Bounds in either format
 * @returns {boolean} True if bounds are valid
 */
export const isValidBounds = bounds => {
  if (!bounds || !Array.isArray(bounds)) {
    return false
  }

  // Format 1: Flat array [west, south, east, north]
  if (bounds.length === 4 && bounds.every(val => typeof val === "number")) {
    return bounds.every(val => Number.isFinite(val))
  }

  // Format 2: Nested array [[lng1, lat1], [lng2, lat2]]
  if (bounds.length === 2) {
    const [sw, ne] = bounds
    if (!Array.isArray(sw) || !Array.isArray(ne)) {
      return false
    }

    if (sw.length !== 2 || ne.length !== 2) {
      return false
    }

    // Check all values are finite numbers (not NaN, not Infinity)
    return sw.every(val => Number.isFinite(val)) && ne.every(val => Number.isFinite(val))
  }

  return false
}

export const fitToBounds = (map, bbox, options) => {
  if (!map || !bbox) {
    return
  }

  // Validate bounds contain valid numbers to prevent NaN crashes
  if (!isValidBounds(bbox)) {
    console.warn("Invalid bounds detected, skipping fitToBounds:", bbox)
    return
  }

  if (!options) {
    options = {}
  }

  if (options.maxZoom == null) {
    options.maxZoom = MAX_MAP_ZOOM
  }

  map.fitBounds(bbox, options)
}

export const getSmallestFeature = features => {
  const points = []
  const polygons = []

  for (const feature of features) {
    if (feature.geometry.type === "Point") {
      points.push(feature)
      continue
    }

    if (feature.geometry.type === "Polygon") {
      polygons.push(feature)
    }
  }

  const point = points.reduce((accumulator, value) => {
    if (accumulator?.feature?.floor == null) {
      return value
    }

    return accumulator.feature.floor < value.feature.floor ? accumulator : value
  }, null)

  const polygon = polygons
    .map(f => ({ feature: f, size: area(f) }))
    .reduce((accumulator, value) => {
      if (accumulator?.feature == null) {
        return value
      }

      return accumulator.size < value.size ? accumulator : value
    }, null)

  return { point, polygon: polygon?.feature }
}

export const PolygonPointOption = {
  TOP_LEFT: 0,
  TOP_CENTER: 1,
  TOP_RIGHT: 2,
  RIGHT: 3,
  BOTTOM_RIGHT: 4,
  BOTTOM_CENTER: 5,
  BOTTOM_LEFT: 6,
  LEFT: 7,
  CENTER: 8,
}

export const getPointOnPolygon = (polygon, option = PolygonPointOption.TOP_CENTER) => {
  const boundingBox = bbox(polygon)
  if (!boundingBox) {
    return null
  }

  const minLongitude = Math.min(boundingBox[0], boundingBox[2])
  const maxLongitude = Math.max(boundingBox[0], boundingBox[2])
  const minLatitude = Math.min(boundingBox[1], boundingBox[3])
  const maxLatitude = Math.max(boundingBox[1], boundingBox[3])

  switch (option) {
    case PolygonPointOption.TOP_LEFT:
      return new Point([minLongitude, maxLatitude])

    case PolygonPointOption.TOP_CENTER:
      return new Point([minLongitude + (maxLongitude - minLongitude) / 2, maxLatitude])

    case PolygonPointOption.TOP_RIGHT:
      return new Point([maxLongitude, maxLatitude])

    case PolygonPointOption.RIGHT:
      return new Point([maxLongitude, minLatitude + (maxLatitude - minLatitude) / 2])

    case PolygonPointOption.BOTTOM_RIGHT:
      return new Point([maxLongitude, minLatitude])

    case PolygonPointOption.BOTTOM_CENTER:
      return new Point([minLongitude + (maxLongitude - minLongitude) / 2, minLatitude])

    case PolygonPointOption.BOTTOM_LEFT:
      return new Point([minLongitude, minLatitude])

    case PolygonPointOption.LEFT:
      return new Point([minLongitude, minLatitude + (maxLatitude - minLatitude) / 2])

    case PolygonPointOption.CENTER:
      return new Point([
        minLongitude + (maxLongitude - minLongitude) / 2,
        minLatitude + (maxLatitude - minLatitude) / 2,
      ])

    default:
      throw new Error(`Unsupported option: ${option}`)
  }
}

/**
 * Pan the map to the center of the given features while preserving zoom level.
 * Uses flyTo for smooth animation instead of fitBounds which resets zoom.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL map instance
 * @param {GeoJSON.FeatureCollection|GeoJSON.Feature} features - Features to center on
 * @param {Object} options - Optional flyTo options (duration, padding, etc.)
 */
export const panToZoneCenter = (map, features, options = {}) => {
  if (!map || !features) {
    return
  }

  let centerPoint
  try {
    centerPoint = findCenter(features)
  } catch (error) {
    console.warn("Could not calculate center for features:", error.message)
    return
  }

  if (!centerPoint?.geometry?.coordinates) {
    console.warn("Could not calculate center for features, skipping pan")
    return
  }

  const currentZoom = map.getZoom()
  const [lng, lat] = centerPoint.geometry.coordinates

  map.flyTo({
    center: [lng, lat],
    zoom: currentZoom,
    duration: 500,
    ...options,
  })
}

/**
 * Danger zone threshold in CSS pixels from viewport edges.
 * Shared between calculateScreenAnchor (flow tooltips) and
 * DANGER_ZONE_OFFSETS in default.js (polygon tooltips).
 * Exported so both systems reference one source of truth.
 */
export const SCREEN_ANCHOR_THRESHOLD = 100

/**
 * Calculates the optimal Mapbox popup anchor based on cursor screen position
 * relative to viewport edges. Used for cursor-following popups (e.g., flow tooltips)
 * where the popup position tracks the mouse and needs to flip when near edges.
 *
 * IMPORTANT: All dimensions must be in CSS pixels (not device/render-buffer pixels).
 * Use canvas.clientWidth/clientHeight (not canvas.width/height) to avoid HiDPI mismatch
 * since e.point reports CSS pixel coordinates.
 *
 * @param {Object} point - CSS pixel coordinates {x, y} from map event e.point
 * @param {number} canvasWidth - Map canvas CSS width (canvas.clientWidth)
 * @param {number} canvasHeight - Map canvas CSS height (canvas.clientHeight)
 * @param {Object} [padding] - Map padding {top, bottom, left, right} in CSS pixels
 * @param {Object} [options] - Additional options
 * @param {number} [options.topThreshold] - Override top-edge threshold (CSS pixels).
 *   Use for tall popups (e.g., flow tooltips with 300px max-height) that need to flip
 *   anchor further from the top edge than the default SCREEN_ANCHOR_THRESHOLD (ENG-5462).
 * @param {number} [options.bottomThreshold] - Override bottom-edge threshold (CSS pixels).
 * @returns {string} Mapbox popup anchor string (e.g., "bottom", "top-left")
 */
export function calculateScreenAnchor(point, canvasWidth, canvasHeight, padding = {}, options = {}) {
  const pad = {
    top: padding.top || 0,
    bottom: padding.bottom || 0,
    left: padding.left || 0,
    right: padding.right || 0,
  }

  const topThreshold = options.topThreshold ?? SCREEN_ANCHOR_THRESHOLD
  const bottomThreshold = options.bottomThreshold ?? SCREEN_ANCHOR_THRESHOLD

  const nearTop = point.y < pad.top + topThreshold
  const nearBottom = point.y > canvasHeight - pad.bottom - bottomThreshold
  const nearLeft = point.x < pad.left + SCREEN_ANCHOR_THRESHOLD
  const nearRight = point.x > canvasWidth - pad.right - SCREEN_ANCHOR_THRESHOLD

  // Corner cases
  if (nearTop && nearLeft) return "top-left"
  if (nearTop && nearRight) return "top-right"
  if (nearBottom && nearLeft) return "bottom-left"
  if (nearBottom && nearRight) return "bottom-right"

  // Edge cases
  if (nearTop) return "top"
  if (nearBottom) return "bottom"
  if (nearLeft) return "left"
  if (nearRight) return "right"

  // Default: popup above cursor
  return "bottom"
}
